import { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { ADDRESS_PAGE } from '@constants/formNames';
import { SUBMITTED_ADDRESS_PAGE_VALUES } from '@constants/storageKeys';
import { AddressPageFormValues } from '@features/Address/types';
import { SessionWrapper } from '@features/SessionRestore';
import { removeAddressSessionStorage } from '@utils/storagePropertyManagement';

import { type LocalSessionAddressContextState } from '../types';
import { LocalSessionContext } from './Context';

interface LocalSessionAddressProviderProps {
  children: ReactNode;
}

const LocalSessionAddressProvider = ({
  children,
}: LocalSessionAddressProviderProps) => {
  const [address, setAddress] = useState<AddressPageFormValues>(null);

  const resetAddressStorage = useCallback(() => {
    setAddress(null);
    removeAddressSessionStorage();
  }, []);

  const updateAddressPageStorage = useCallback(
    (addressData: AddressPageFormValues) => {
      if (!addressData) {
        return null;
      }

      const parsedAddress = JSON.stringify(addressData);

      window.sessionStorage.setItem(
        SUBMITTED_ADDRESS_PAGE_VALUES,
        parsedAddress
      );
      window.sessionStorage.setItem(ADDRESS_PAGE, parsedAddress);

      return true;
    },
    []
  );

  // After session resumes, this loads address page input
  // This would be loaded by the FormikPersist component when the user navigates back to the address page
  // and initial values comparison when submitting address page
  const handleAddressPageState = useCallback(() => {
    const isUpdated = updateAddressPageStorage(address);

    if (isUpdated) {
      setAddress(null);
    }
  }, [address, updateAddressPageStorage]);

  const stateValues: LocalSessionAddressContextState = useMemo(
    () => ({
      address,
      setAddress,
      handleAddressPageState,
      updateAddressPageStorage,
      resetAddressStorage,
    }),
    [
      address,
      handleAddressPageState,
      updateAddressPageStorage,
      resetAddressStorage,
    ]
  );

  return (
    <LocalSessionContext value={stateValues}>
      <SessionWrapper>{children}</SessionWrapper>
    </LocalSessionContext>
  );
};

export { LocalSessionAddressProvider };
